/**
 * presence.ts — live device presence over a WebSocket (architecture §4.5:
 * Redis TTL heartbeats → pub/sub deltas to the console; never poll Postgres).
 *
 * The console subscribes to a presence WS; the broker pushes deltas as devices
 * come online / go offline. Presence is org-scoped server-side (the JWT carries
 * org_id) — the client never sees another tenant's devices (SPEC §11.1).
 *
 * Wire (presence WS): JSON messages
 *   { type:"snapshot", devices:[{device_id, online, last_seen_at}] }
 *   { type:"delta",    device_id, online, last_seen_at }
 */

export interface PresenceDelta {
  device_id: string;
  online: boolean;
  last_seen_at: string | null;
}

type PresenceMsg =
  | { type: 'snapshot'; devices: PresenceDelta[] }
  | ({ type: 'delta' } & PresenceDelta);

export interface PresenceOptions {
  token: string; // access JWT (org-scoped)
  onSnapshot?: (devices: PresenceDelta[]) => void;
  onDelta?: (delta: PresenceDelta) => void;
  onStatus?: (connected: boolean) => void;
}

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

function presenceUrl(): string {
  // Same-origin in prod (behind nginx); honors VITE_API_BASE if set.
  const base = API_BASE || window.location.origin;
  const u = new URL(base);
  u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
  u.pathname = '/api/v1/presence';
  return u.toString();
}

/**
 * Open a reconnecting presence subscription. Returns a disposer.
 */
export function subscribePresence(opts: PresenceOptions): () => void {
  let ws: WebSocket | null = null;
  let closed = false;
  let backoff = 1000;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const connect = () => {
    if (closed) return;
    // M6: token in the Sec-WebSocket-Protocol header, not the URL query.
    ws = new WebSocket(presenceUrl(), ['helm-bearer', opts.token]);
    ws.onopen = () => {
      backoff = 1000;
      opts.onStatus?.(true);
    };
    ws.onmessage = (ev) => {
      let msg: PresenceMsg;
      try {
        msg = JSON.parse(ev.data as string) as PresenceMsg;
      } catch {
        return;
      }
      if (msg.type === 'snapshot') opts.onSnapshot?.(msg.devices);
      else if (msg.type === 'delta')
        opts.onDelta?.({
          device_id: msg.device_id,
          online: msg.online,
          last_seen_at: msg.last_seen_at,
        });
    };
    ws.onclose = () => {
      opts.onStatus?.(false);
      if (closed) return;
      reconnectTimer = setTimeout(connect, backoff);
      backoff = Math.min(backoff * 2, 30_000);
    };
    ws.onerror = () => ws?.close();
  };

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    ws?.close();
  };
}
